import Image from "next/image";

import { Button, Card } from "@chef/components";
import { ADDON_SUBSCRIPTIONS, BRAND_NAME, BRAND_PATHS } from "@chef/constants";
import {
  useBillingQuery,
  useUpdateBillingAgreementAddonsMutation,
} from "@chef/state-management";
import { useConfirmDialog } from "@chef/hooks";
import { isEqualStrings } from "@chef/utils/equal";
import { Modal } from "@chef/smart-components";

import { intl } from "./RaatassOptOutSection.Intl";

export const RaatassOptOutSection = () => {
  const { data: billingQuery, isFetching: isBillingFetching } =
    useBillingQuery();

  const [updateBillingAddons, { isLoading: isMutating }] =
    useUpdateBillingAgreementAddonsMutation();

  const { handleConfirm, handleCancel, handleShow, isShowing } =
    useConfirmDialog();

  const isLoading = isBillingFetching || isMutating;

  const raatass = billingQuery?.billing.addonSubscriptions?.find(
    (s) => s.subscriptionId === ADDON_SUBSCRIPTIONS.RAATASS,
  );

  const handleOptOut = async () => {
    if (!billingQuery) {
      return;
    }

    const addonSubscriptions = billingQuery.billing.addonSubscriptions;

    // Check if the user already has a Råtass subscription
    // If so, make sure it's inactive
    const updatedAddonSubscriptions = addonSubscriptions.map((sub) => {
      if (isEqualStrings(sub.subscriptionId, ADDON_SUBSCRIPTIONS.RAATASS)) {
        return {
          addonSubscriptionId: sub.subscriptionId,
          active: false,
        };
      }

      return { addonSubscriptionId: sub.subscriptionId, active: sub.active };
    });

    await updateBillingAddons(updatedAddonSubscriptions);
  };

  if (!["AMK", "GL"].includes(BRAND_NAME) || !raatass?.active) {
    return null;
  }

  return (
    <>
      <Modal open={isShowing} onClose={handleCancel} size="md">
        <Card className="flex flex-col gap-4 p-4 mt-20 text-center md:mt-40">
          <h1 className="text-xl md:text-2xl">
            <strong>Bekreft utmelding</strong>
          </h1>

          <p className="text-sm grow">
            Du er i ferd med å melde deg ut av Råtass. Dette betyr at du ikke
            lenger støtter ditt idrettslag gjennom vårt samarbeid.
          </p>

          <div className="flex flex-col gap-4">
            <Button onClick={handleConfirm} loading={isLoading} primary>
              Bekreft
            </Button>
            <Button onClick={handleCancel} disabled={isLoading} outlined>
              Avbryt
            </Button>
          </div>
        </Card>
      </Modal>
      <Card id="råtass-status-card">
        <div className="flex gap-4">
          <div className="flex flex-col gap-3">
            <h2 className="text-xl">
              <strong>Råtass</strong>
            </h2>
            <p className="text-sm">{intl.RAATASS_DESCRIPTION}</p>

            <p className="text-sm">
              Les mer på{" "}
              <a
                href={BRAND_PATHS.RAATASS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                raatass.no
              </a>
            </p>

            <p>
              <button
                onClick={() => handleShow(handleOptOut)}
                className="text-sm text-left underline"
              >
                {intl.OPT_OUT_OF_RAATASS}
              </button>
            </p>
          </div>
          <a
            className="py-4 pl-4 md:p-8 shrink-0"
            href={BRAND_PATHS.RAATASS_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src="https://ggfrontendassets.azureedge.net/publicassets/ratass_logo_color_1000px.svg"
              alt="Råtass Logo"
              width={96}
              height={96}
            />
          </a>
        </div>
      </Card>
    </>
  );
};
